import { observer } from 'mobx-react';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Table } from '../../components';
import { useStore } from '../../core/utils/hook';
import { canShowTrainingListMenu } from '../../core/utils/gaMenuPermissions';
import {
    CERTIFICATE_BULK_EXPORT_THRESHOLD,
    DATE_YMD,
    DOWNLOAD_ATTENDANCE_HISTORY_TYPE,
    SYSTEM_PATH
} from '../../core/configs/constants';
import { handleDownloadFile } from '../../core/utils/file';
import ProfileLayout from '../Profile/components/ProfileLayout';

// Per Figma spec (#17889 follow-up): the GA searches/paginates the eligible-student list and
// explicitly picks who to download a certificate for via checkboxes — download is no longer
// "everyone eligible for this ticket plan" automatically.
const CertificateDownloadScreen = observer(() => {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    // `location.state` only survives a click-through from the training list — it comes back empty
    // on a direct URL load / page refresh, leaving 受講プラン blank. Used only as an instant-paint
    // fallback before `fetchData` below resolves `ticketName` from the API (the reliable source,
    // always available regardless of how the GA arrived at this page).
    const [ticketPlanName, setTicketPlanName] = useState(location.state?.ticketPlanName || '');

    const {
        courseStore: {
            getCompletionSettingsForGA,
            getEligibleStudentsForCertificate, clearEligibleStudentsForCertificate,
            eligibleStudentsForCertificate, pagingEligibleStudentsForCertificate, useEmployeeCodeForCertificate
        },
        groupAdminStore: { startCertificateExportJob },
        userStore: { userInfoHeader, getUserInfoHeader },
        modalStore: { openAlert, openErrorModal, openWarningModal, openProcessingModal, closeProcessingModal }
    } = useStore();

    const [certificateName, setCertificateName] = useState('');
    const [targetCourseIds, setTargetCourseIds] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);

    const [searchStudentName, setSearchStudentName] = useState('');
    const [searchEmail, setSearchEmail] = useState('');
    const [searchEmployeeCode, setSearchEmployeeCode] = useState('');

    const [selectedStudents, setSelectedStudents] = useState([]);
    const [isSelectedAllCustom, setIsSelectedAllCustom] = useState(false);
    const [toggleClearSelected, setToggleClearSelected] = useState(false);

    const selectedRowIds = useMemo(() => {
        const obj = {};
        selectedStudents.forEach(s => { obj[s.id] = true; });
        return obj;
    }, [selectedStudents]);

    useEffect(() => {
        if (!userInfoHeader?.id) {
            getUserInfoHeader();
            return;
        }
        if (!canShowTrainingListMenu(userInfoHeader)) {
            navigate(SYSTEM_PATH.HOME, { replace: true });
            return;
        }
        if (!ticketId || isNaN(ticketId)) {
            navigate(SYSTEM_PATH.COMPANY_TRAINING_LIST);
            return;
        }
        fetchData();

        return () => clearEligibleStudentsForCertificate();
    }, [ticketId, userInfoHeader?.id, userInfoHeader?.isAllowTrainingList]);

    const fetchData = async () => {
        const data = await getCompletionSettingsForGA(ticketId);
        const courseIds = (data?.courses || []).filter(c => c.isChecked).map(c => c.id);
        setCertificateName(data?.completionCertificateName || '');
        // Overwrite the location.state fallback with the API's own value once it resolves — the
        // reliable source, unlike location.state which is empty on direct load/refresh.
        if (data?.ticketName) setTicketPlanName(data.ticketName);
        setTargetCourseIds(courseIds);
        fetchStudents({ page: 1 });
    };

    // hideLoading=true on plain pagination/page-size changes (table shows its own state);
    // the initial load and explicit search still show the global spinner.
    const fetchStudents = async (tableState = {}, hideGlobalLoading = false) => {
        await getEligibleStudentsForCertificate(ticketId, {
            studentName: searchStudentName || undefined,
            email: searchEmail || undefined,
            employeeCode: searchEmployeeCode || undefined,
            ...tableState
        }, hideGlobalLoading);
    };

    const onFetchTable = (tableState) => {
        fetchStudents(tableState, true);
    };

    const onSearch = (e) => {
        e.preventDefault();
        setSelectedStudents([]);
        setIsSelectedAllCustom(false);
        fetchStudents({ page: 1 });
    };

    const onSelectedAllChange = (value) => {
        setSelectedStudents(old => {
            let data = old;
            eligibleStudentsForCertificate.forEach(row => {
                const index = data.findIndex(e => e.id === row.id);
                if (value) {
                    if (index < 0) data = [...data, row];
                } else {
                    if (index > -1) data = [...data.slice(0, index), ...data.slice(index + 1)];
                }
            });
            return data;
        });
        setIsSelectedAllCustom(value);
    };

    const onSelectedChange = (selectedRowIdsMap) => {
        if (!selectedRowIdsMap) {
            setSelectedStudents([]);
            setIsSelectedAllCustom(false);
            return;
        }

        const selectedIds = Object.keys(selectedRowIdsMap);
        setSelectedStudents(old => {
            let data = old;
            eligibleStudentsForCertificate.forEach(row => {
                const index = data.findIndex(e => e.id === row.id);
                if (selectedIds.includes(String(row.id))) {
                    if (index < 0) data = [...data, row];
                } else {
                    if (index > -1) data = [...data.slice(0, index), ...data.slice(index + 1)];
                }
            });

            const dataIds = data.map(e => e.id);
            setIsSelectedAllCustom(
                eligibleStudentsForCertificate.length > 0 &&
                eligibleStudentsForCertificate.every(e => dataIds.includes(e.id))
            );
            return data;
        });
    };

    const clearSelection = () => {
        setToggleClearSelected(v => !v);
        setSelectedStudents([]);
        setIsSelectedAllCustom(false);
    };

    const handleAfterDownload = (msg) => {
        // #17917 — closes the 処理中 spinner modal opened in executeDownload below; the error
        // modal (if any) stacks fine on top since modalStore.instances is already a stack.
        closeProcessingModal();
        if (msg) {
            openErrorModal('エラー', msg);
        }
        setIsDownloading(false);
    };

    // Button itself is disabled unless >=1 student is selected (see JSX), so these are just a
    // last-resort guard against a stale click; the actual download only starts after the GA
    // confirms the "選択した◯名の...よろしいですか？" dialog (spec, #17889 Figma).
    const handleDownload = () => {
        if (isDownloading) return;

        if (!targetCourseIds.length) {
            openAlert('修了証の発行対象となる講座が設定されていません。先に「修了証設定」で対象の講座を選択してください。');
            return;
        }
        if (!selectedStudents.length) {
            openAlert('ダウンロードする受講者を選択してください。');
            return;
        }

        openWarningModal(
            `選択した${selectedStudents.length}名の受講者の修了証明書をダウンロードしてもよろしいですか？`,
            executeDownload,
            '確認',
            'small',
            'はい',
            'いいえ'
        );
    };

    const executeDownload = async () => {
        const userIds = selectedStudents.map(s => s.id);
        setIsDownloading(true);

        if (userIds.length <= CERTIFICATE_BULK_EXPORT_THRESHOLD) {
            // #17917 — dedicated "処理中" spinner (Figma 6152-6657) instead of the app-wide
            // elephant loading screen (apiStore.showLoading), which read as a generic/unrelated
            // full-page loading state rather than communicating "your PDF export is in progress,
            // don't navigate away" the way this flow specifically needs.
            openProcessingModal();
            handleDownloadFile({
                url: 'course/attendance/export-pdf',
                data: {
                    typeExport: DOWNLOAD_ATTENDANCE_HISTORY_TYPE.TICKET_PLAN,
                    userIds,
                    courseIds: targetCourseIds,
                    // #17889 fix — backend needs this to resolve the ticketplan's own cert settings
                    // (certificate name / employee-code display), same as the async job path already sends.
                    ticketId: Number(ticketId)
                },
                cb: handleAfterDownload
            });
            return;
        }

        // Large selection — generating every PDF synchronously in one request would risk hitting
        // the proxy's request timeout, so hand off to a background job (ExportCertificateJobWorker)
        // and hand progress display to the corner widget (ExportJobsWidget) instead of blocking
        // this screen — the job (and its progress polling) now lives on groupAdminStore, so it
        // keeps running/updating even if the GA navigates away. The server re-validates every id
        // against the same eligibility rule as this screen's own list (see createExportCertificateJob).
        const res = await startCertificateExportJob(ticketId, targetCourseIds, userIds, ticketPlanName);
        setIsDownloading(false);
        if (!res?.ok) {
            openErrorModal('エラー', res?.message || 'エクスポートジョブの作成に失敗しました。');
        }
    };

    const handleCancel = () => {
        navigate(SYSTEM_PATH.COMPANY_TRAINING_LIST + '?tab=ticket_plan');
    };

    const columns = useMemo(() => {
        const cols = [];
        if (useEmployeeCodeForCertificate) {
            cols.push({
                Header: '社員番号',
                accessor: 'employeeCode',
                disableSortBy: true,
                Cell: ({ row: { original } }) => <div>{original?.employeeCode || '--'}</div>,
                width: '120'
            });
        }
        cols.push(
            {
                Header: '受講者名',
                accessor: 'firstName',
                disableSortBy: true,
                // Order must match the backend search concatenation (first_name || ' ' || last_name,
                // see TicketService._buildCertificateEligibilitySql) — otherwise typing the exact
                // displayed name (e.g. "U1383 User") fails to match even though it's shown verbatim.
                Cell: ({ row: { original } }) => <div>{original?.firstName} {original?.lastName}</div>,
                width: '160'
            },
            {
                Header: 'メールアドレス',
                accessor: 'email',
                disableSortBy: true,
                Cell: ({ row: { original } }) => <div>{original?.email}</div>,
                width: '220'
            },
            {
                Header: '受講開始日',
                accessor: 'startDate',
                disableSortBy: true,
                Cell: ({ row: { original } }) => (
                    <div>{original?.startDate ? moment(original.startDate).format(DATE_YMD) : '--'}</div>
                ),
                width: '130'
            },
            {
                Header: '受講終了日',
                accessor: 'endDate',
                disableSortBy: true,
                Cell: ({ row: { original } }) => (
                    <div>{original?.endDate ? moment(original.endDate).format(DATE_YMD) : '--'}</div>
                ),
                width: '130'
            }
        );
        return cols;
    }, [useEmployeeCodeForCertificate, eligibleStudentsForCertificate, isSelectedAllCustom, toggleClearSelected, selectedRowIds]);

    return (
        <ProfileLayout titlePage='修了証ダウンロード'>
            <div className='detail-count-info-right completion-settings certificate-download-screen'>
                <div className='title-title'>
                    <h2 className='title-2'>修了証ダウンロード</h2>
                </div>

                <div className='completion-settings-content'>
                    <div className='content-student-detail'>
                        <div className='item'>
                            <div className='left'>
                                <label>受講プラン</label>
                            </div>
                            <div className='right'>
                                <span>{ticketPlanName}</span>
                            </div>
                        </div>

                        <div className='item'>
                            <div className='left'>
                                <label>修了証名</label>
                            </div>
                            <div className='right'>
                                <span className='certificate-name-value'>{certificateName || '--'}</span>
                            </div>
                        </div>
                    </div>

                    <div className='student-picker-section'>
                        <div className='student-picker-header'>受講者を選択</div>
                        <div className='student-picker-note'>修了証をダウンロードする受講者を検索し、選択してください。</div>

                        <form className='student-picker-search' onSubmit={onSearch}>
                            {/* Named search-row (not the generic "row") — bootstrap.min.css (loaded
                                globally via public/index.html) defines its own .row grid class with
                                a -12px negative margin for gutters, which was silently overflowing
                                this div 12px past its own parent on both sides and defeating any
                                padding we set on it (user-annotated screenshot, 2026-08-12). */}
                            <div className='search-row'>
                                <div className='item'>
                                    <label htmlFor='cert-search-name'>受講者名</label>
                                    <input
                                        id='cert-search-name'
                                        type='text'
                                        value={searchStudentName}
                                        onChange={e => setSearchStudentName(e.target.value)}
                                    />
                                </div>
                                <div className='item'>
                                    <label htmlFor='cert-search-email'>メールアドレス</label>
                                    <input
                                        id='cert-search-email'
                                        type='text'
                                        value={searchEmail}
                                        onChange={e => setSearchEmail(e.target.value)}
                                    />
                                </div>
                                {useEmployeeCodeForCertificate && (
                                    <div className='item'>
                                        <label htmlFor='cert-search-employee-code'>社員番号</label>
                                        <input
                                            id='cert-search-employee-code'
                                            type='text'
                                            value={searchEmployeeCode}
                                            onChange={e => setSearchEmployeeCode(e.target.value)}
                                        />
                                    </div>
                                )}
                                <button type='submit' className='search-submit-btn' aria-label='検索'>
                                    <i className='fa-solid fa-magnifying-glass'></i>
                                </button>
                            </div>
                        </form>

                        {eligibleStudentsForCertificate.length > 0 ? (
                            <Table
                                columns={columns}
                                data={eligibleStudentsForCertificate}
                                enableServerSidePaging
                                initialTableState={pagingEligibleStudentsForCertificate}
                                onFetch={onFetchTable}
                                enableSelectRow
                                onSelectedChange={onSelectedChange}
                                selectedRowIds={selectedRowIds}
                                rowKey='id'
                                useCustomSelectedAll
                                isSelectedAllCustom={isSelectedAllCustom}
                                onSelectedAllChange={onSelectedAllChange}
                                className='student-picker-table'
                                // Columns sum to 810px (checkbox 50 + 社員番号 120 + 受講者名 160 +
                                // メールアドレス 220 + 受講開始日/終了日 130×2) — without this, ReactTable
                                // lets the table shrink to fit any container width, squeezing every
                                // column unreadable on mobile instead of keeping them full-size and
                                // scrolling (see .student-picker-table CSS for the scroll container
                                // fix itself; same `scrollHorizontalWidth` pattern already used by
                                // PendingApprovalStudentListScreen.js and friends).
                                scrollHorizontalWidth={'810px'}
                                // Renders inside react-table's own .selection-header row, next to the
                                // 表示件数 page-size dropdown, so the two sit on the same line (#17901).
                                element={selectedStudents.length > 0 && (
                                    <div className='student-picker-selected-info'>
                                        <span>{selectedStudents.length}名を選択中</span>
                                        <span role='button' onClick={clearSelection}>選択をクリア</span>
                                    </div>
                                )}
                            />
                        ) : (
                            <div className='student-picker-empty'>
                                修了証明書のダウンロード条件を満たす受講者が存在しません。
                            </div>
                        )}
                    </div>

                    {/* Background job progress is shown in the corner widget (ExportJobsWidget,
                        rendered at app root) instead of inline here — see startCertificateExportJob
                        in groupAdminStore for why it needed to move out of this screen's state. */}

                    <div className='completion-settings-footer'>
                        <button
                            type='button'
                            className='btn-cancel'
                            onClick={handleCancel}
                        >
                            キャンセル
                        </button>
                        <button
                            type='button'
                            className='btn-submit'
                            onClick={handleDownload}
                            disabled={isDownloading || !targetCourseIds.length || !selectedStudents.length}
                        >
                            ダウンロード
                        </button>
                    </div>
                </div>
            </div>
        </ProfileLayout>
    );
});

export default CertificateDownloadScreen;
